import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HostingServerDTO, UpdateHostingServerInput } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { DataTable, type Column } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import { useAuth } from "@/auth/auth";
import { Btn, Chip, Field, Panel, SectionHeader, inputClass } from "@/design/primitives";

const t = cs.hostingServers;

interface FormState {
  id: string | null;
  name: string;
  apiUrl: string;
  token: string;
  note: string;
  isDefault: boolean;
  ownerId: string;
}

const EMPTY: FormState = { id: null, name: "", apiUrl: "", token: "", note: "", isDefault: false, ownerId: "" };

/**
 * Coolify servery, na kterých běží projekty — vlastní server ownera i servery
 * jiných uživatelů. Owner vidí a spravuje všechny, ostatní jen své. Token se
 * jen zapisuje: zpátky se ukazuje poslední čtyřka.
 */
export function HostingServersPanel() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const isOwner = session?.user.role === "OWNER";
  const servers = useQuery({ queryKey: ["hosting-servers"], queryFn: api.hostingServers });
  const users = useQuery({ queryKey: ["users"], queryFn: api.users, enabled: isOwner });
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<string, string>>({});

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["hosting-servers"] });
  const errText = (err: unknown) => (err instanceof ApiRequestError ? err.message : cs.common.error);

  const save = useMutation({
    mutationFn: (f: FormState) => {
      const input: UpdateHostingServerInput = {
        name: f.name.trim(),
        apiUrl: f.apiUrl.trim(),
        note: f.note.trim(),
        ...(f.token.trim() ? { token: f.token.trim() } : {}),
        ...(isOwner ? { isDefault: f.isDefault, ...(f.ownerId ? { ownerId: f.ownerId } : {}) } : {}),
      };
      return f.id
        ? api.updateHostingServer(f.id, input)
        : api.createHostingServer({ ...input, name: input.name!, apiUrl: input.apiUrl! });
    },
    onSuccess: async () => {
      setForm(null);
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(errText(err)),
  });

  const check = useMutation({
    mutationFn: (id: string) => api.checkHostingServer(id),
    onSuccess: async (res, id) => {
      setChecked((c) => ({ ...c, [id]: res.ok ? t.checkOk(res.version) : `⚠ ${res.error ?? ""}` }));
      await invalidate();
    },
    onError: (err, id) => setChecked((c) => ({ ...c, [id]: `⚠ ${errText(err)}` })),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteHostingServer(id),
    onSuccess: invalidate,
    onError: (err) => setError(errText(err)),
  });

  const list = servers.data?.servers ?? [];
  const edit = (s: HostingServerDTO) =>
    setForm({ id: s.id, name: s.name, apiUrl: s.apiUrl, token: "", note: s.note, isDefault: s.isDefault, ownerId: s.owner?.id ?? "" });

  return (
    <Panel className="p-5" data-testid="hosting-servers">
      <SectionHeader
        label={t.header}
        right={
          form ? null : (
            <Btn onClick={() => setForm({ ...EMPTY, isDefault: isOwner && list.length === 0 })} data-testid="hosting-server-new">
              {t.add}
            </Btn>
          )
        }
      />
      <p className="mt-2 text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {t.note}
      </p>

      {form ? (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t.name}>
            <input
              className={inputClass}
              value={form.name}
              placeholder="Indra / Šleza"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="hosting-server-name"
              autoFocus
            />
          </Field>
          <Field label={t.apiUrl}>
            <input
              className={inputClass}
              value={form.apiUrl}
              placeholder="https://server.example.cz/api/v1"
              onChange={(e) => setForm({ ...form, apiUrl: e.target.value })}
              data-testid="hosting-server-url"
            />
          </Field>
          <Field label={t.token} hint={form.id ? t.tokenKeep : t.tokenHint}>
            <input
              className={inputClass}
              type="password"
              autoComplete="off"
              value={form.token}
              onChange={(e) => setForm({ ...form, token: e.target.value })}
              data-testid="hosting-server-token"
            />
          </Field>
          <Field label={t.noteLabel}>
            <input className={inputClass} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
          {isOwner ? (
            <>
              <Field label={t.owner}>
                <select
                  className={inputClass}
                  style={{ background: "var(--surface-inset)" }}
                  value={form.ownerId}
                  onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
                >
                  <option value="">{t.ownerSelf}</option>
                  {(users.data?.users ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </select>
              </Field>
              <label className="flex items-center gap-2 self-end pb-2 text-[12px]" style={{ color: "var(--text-secondary)" }}>
                <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
                {t.isDefault}
              </label>
            </>
          ) : null}
          <div className="flex justify-end gap-2 md:col-span-2">
            <Btn onClick={() => { setForm(null); setError(null); }}>{cs.common.cancel}</Btn>
            <Btn
              variant="primary"
              disabled={save.isPending || !form.name.trim() || !form.apiUrl.trim() || (!form.id && !form.token.trim())}
              onClick={() => save.mutate(form)}
              data-testid="hosting-server-save"
            >
              {save.isPending ? t.checking : form.id ? cs.common.save : cs.common.create}
            </Btn>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 text-[12px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}

      <div className="mt-5">
        <DataTable
          columns={([
            {
              key: "name",
              label: `${t.name} · ${t.owner}`,
              width: "minmax(120px, 1fr)",
              primary: true,
              cell: (s) => (
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="break-words">{s.name}</span>
                  {/* vlastník (a VÝCHOZÍ) pod názvem — samostatný sloupec se do panelu nevešel */}
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                    <span className="truncate">{s.owner?.name ?? "—"}</span>
                    {s.isDefault ? <Chip tone="violet">{t.default}</Chip> : null}
                  </span>
                </span>
              ),
            },
            {
              key: "url",
              label: t.apiUrl,
              width: "minmax(170px, 1.3fr)",
              cell: (s) => <span className="af-mono break-all text-[11px]">{s.apiUrl}</span>,
            },
            {
              key: "token",
              label: "TOKEN",
              width: "70px",
              cell: (s) =>
                s.tokenHint ? <span className="af-mono text-[11px]">{s.tokenHint}</span> : <Chip tone="red">{t.noToken}</Chip>,
            },
            {
              key: "state",
              label: t.state,
              width: "minmax(120px, 1fr)",
              cell: (s) => (
                <span className="text-[11px]" style={{ color: s.lastError ? "var(--critical)" : "var(--text-secondary)" }}>
                  {checked[s.id] ?? (s.lastError ? `⚠ ${s.lastError}` : s.lastCheckedAt ? t.ok : "—")}
                </span>
              ),
            },
            {
              key: "projects",
              label: t.projects,
              width: "minmax(110px, 1fr)",
              cell: (s) => (
                <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  {s.projects.map((p) => p.name).join(", ") || (s.isDefault ? t.defaultProjects : "—")}
                </span>
              ),
            },
            {
              key: "actions",
              label: "",
              width: "170px",
              align: "right",
              cell: (s) =>
                s.canManage ? (
                  <span className="flex justify-end gap-2.5 text-[11px]">
                    <button type="button" className="cursor-pointer" style={{ color: "var(--accent)" }} onClick={() => edit(s)}>
                      {t.edit}
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer"
                      style={{ color: "var(--text-secondary)" }}
                      disabled={check.isPending}
                      onClick={() => check.mutate(s.id)}
                    >
                      {t.check}
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer"
                      style={{ color: "var(--critical)" }}
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(t.deleteConfirm(s.name))) remove.mutate(s.id);
                      }}
                    >
                      {t.delete}
                    </button>
                  </span>
                ) : null,
            },
          ] satisfies Column<HostingServerDTO>[])}
          rows={list}
          rowKey={(s) => s.id}
          rowTestId={(s) => `hosting-server-row-${s.id}`}
          stackBelow={1024}
          empty={
            <div className="mt-3 text-[12px]" style={{ color: "var(--text-secondary)" }}>
              {t.empty}
            </div>
          }
        />
      </div>
    </Panel>
  );
}
